import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { googleEvent } from '../utils/dom';
import { useRouter as useRouterIfMounted } from 'next/compat/router';
import { useUiTestArm, useUiTestExposure } from '../components/ui-tests/UiTest';

export type LoginView = 'login' | 'register' | 'forgot' | 'more-info' | 'mobile' | 'otp' | 'login-otp';
export type PostViewState = 'camera' | 'meta-data' | 'review' | 'success' | 'shipping';
export type MobileTopNav = 'left' | 'center' | 'right';
export type SearchViewMode = 'grid' | 'showcase';

export interface UIContextType {
  // Modals and Overlays
  loginOpen: boolean;
  productOpen: boolean;
  chatOpen: boolean;
  popupOpen: boolean;
  popupContents: ReactNode;
  clickClose: (() => void) | null;
  cameraHelpOpen: boolean;
  clientSide?: boolean;
  
  // Drawers and Menus
  reportMenuOpen: boolean;
  messageMenuOpen: boolean;
  chatMenuOpen: boolean;
  chatUserMenuOpen: boolean;
  shareMenuOpen: boolean;
  countryCodeOpen: boolean;
  shippingDrawerOpen: boolean;
  categoryDrawerOpen: boolean;
  /** The left filter drawer (components/filter-drawer.tsx), opened by the search bar's Filter button. */
  filterDrawerOpen: boolean;
  notificationDrawerOpen: boolean;
  
  // UI State
  loginView: LoginView;
  /** Identifier to open the login modal with, or null. Cleared once consumed. */
  loginPrefill: string | null;
  postViewState: PostViewState;
  mobileTopNav: MobileTopNav;
  mobileBottomNav: string;
  searchFocused: boolean;
  uploadSpinner: boolean;
  searchViewMode: SearchViewMode;
  /**
   * False until the saved view preference has been read.
   *
   * `searchViewMode` starts as 'grid' on every load, because the server has no
   * localStorage, and only becomes the reader's saved 'showcase' in an effect
   * after hydration -- one that runs AFTER the page's own effects. A page that
   * decides what to fetch from `searchViewMode` on mount therefore always sees
   * 'grid'. Home did: it fetched the grid list for every showcase reader and
   * painted it as thin rows before the real ones replaced them.
   */
  searchViewModeSettled: boolean;
  
  // UI Actions
  setLoginOpen: (open: boolean) => void;
  setLoginPrefill: (identifier: string | null) => void;
  setProductOpen: (open: boolean) => void;
  setChatOpen: (open: boolean, id?: string | null) => Promise<void>;
  setPopupOpen: (open: boolean) => void;
  setPopupContents: (contents: ReactNode, clickClose?: (() => void) | null) => void;
  setCameraHelpOpen: (open: boolean) => void;
  
  setReportMenuOpen: (open: boolean) => void;
  setMessageMenuOpen: (open: boolean) => void;
  setChatMenuOpen: (open: boolean) => void;
  setChatUserMenuOpen: (open: boolean) => void;
  setShareMenuOpen: (open: boolean) => void;
  setCountryCodeOpen: (open: boolean) => void;
  setShippingDrawerOpen: (open: boolean) => void;
  setCategoryDrawerOpen: (open: boolean) => void;
  setFilterDrawerOpen: (open: boolean) => void;
  setNotificationDrawerOpen: (open: boolean) => void;
  
  setLoginView: (view: LoginView) => void;
  setPostViewState: (state: PostViewState) => void;
  setMobileTopNav: (nav: MobileTopNav) => void;
  setMobileBottomNav: (nav: string) => void;
  setSearchFocus: (focused: boolean) => void;
  setUploadSpinner: (show: boolean) => void;
  setSearchViewMode: (mode: SearchViewMode) => void;
}


/**
 * The pages whose listings draw as grid or showcase rows, by route pattern:
 * home, a category, For You. The start view is settled on whatever page a
 * visitor lands on, but they have only SEEN the arm once one of these renders
 * it; someone who arrives on a listing from search and leaves is not counted.
 */
export const VIEW_ARM_PAGES: ReadonlySet<string> = new Set(['/', '/browse/[category]', '/foryou']);

const UIContext = createContext<UIContextType | undefined>(undefined);

export const useUI = () => {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
};

interface UIProviderProps {
  children: ReactNode;
  initialLoginOpen?: boolean;
  initialMobileTopNav?: MobileTopNav;
}

export const UIProvider: React.FC<UIProviderProps> = ({
  children,
  initialLoginOpen = false,
  initialMobileTopNav = 'left'
}) => {
  // Modals and Overlays
  const [loginOpen, setLoginOpenState] = useState(initialLoginOpen);
  const [productOpen, setProductOpenState] = useState(false);
  const [chatOpen, setChatOpenState] = useState(false);
  const [popupOpen, setPopupOpenState] = useState(false);
  const [popupContents, setPopupContentsState] = useState<ReactNode>('');
  const [clickClose, setClickClose] = useState<(() => void) | null>(null);
  const [cameraHelpOpen, setCameraHelpOpenState] = useState(false);

  // Drawers and Menus
  const [reportMenuOpen, setReportMenuOpenState] = useState(false);
  const [messageMenuOpen, setMessageMenuOpenState] = useState(false);
  const [chatMenuOpen, setChatMenuOpenState] = useState(false);
  const [chatUserMenuOpen, setChatUserMenuOpenState] = useState(false);
  const [shareMenuOpen, setShareMenuOpenState] = useState(false);
  const [countryCodeOpen, setCountryCodeOpenState] = useState(false);
  const [shippingDrawerOpen, setShippingDrawerOpenState] = useState(false);
  const [categoryDrawerOpen, setCategoryDrawerOpenState] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpenState] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpenState] = useState(false);

  // UI State
  const [loginView, setLoginViewState] = useState<LoginView>('login');
  /**
   * An email or phone to open the login modal already holding.
   *
   * The identifier otherwise lives in `useLoginForm`'s own state, so nothing
   * outside the modal can seed it. Checkout needs to: after a guest buys, the
   * webhook has already made them an account under the address they typed, and
   * making them retype it to get into their own order is the kind of friction
   * that sends people to support instead.
   */
  const [loginPrefill, setLoginPrefillState] = useState<string | null>(null);
  const [postViewState, setPostViewStateState] = useState<PostViewState>('meta-data');
  const [mobileTopNav, setMobileTopNavState] = useState<MobileTopNav>(initialMobileTopNav as MobileTopNav);
  const [mobileBottomNav, setMobileBottomNavState] = useState('');
  const [searchFocused, setSearchFocusedState] = useState(false);
  const [uploadSpinner, setUploadSpinnerState] = useState(false);
  const [searchViewMode, setSearchViewModeState] = useState<SearchViewMode>('grid');
  const [searchViewModeSettled, setSearchViewModeSettled] = useState(false);

  // The view a visitor starts in: their saved choice, else the home-default-view
  // UI test's arm (utils/ui-tests/registry.ts). Undefined until storage is read.
  const [savedView, setSavedView] = useState<SearchViewMode | null | undefined>(undefined);
  // The arm actually applied, so only a visitor who started in it is counted.
  // Stays null for a saved choice, or when the arm came too late to use.
  const [appliedViewArm, setAppliedViewArm] = useState<string | null>(null);
  const viewArm = useUiTestArm('home-default-view', { exposed: false });
  // Counted only when what they started in is what the server says now: a
  // cached arm from an earlier run must not count them in an arm they did not see.
  // Null outside a router (isolated renders): nothing is counted there.
  const router = useRouterIfMounted();
  const onViewPage = VIEW_ARM_PAGES.has(router?.pathname ?? '');
  useUiTestExposure('home-default-view', onViewPage && savedView === null && appliedViewArm !== null && appliedViewArm === viewArm);

  // Load saved view preference from localStorage after hydration
  useEffect(() => {
    let saved: SearchViewMode | null = null;
    try {
      const stored = localStorage.getItem('search-view-preference');
      if (stored === 'grid' || stored === 'showcase') saved = stored;
    // eslint-disable-next-line sellwild-code/catch-must-log -- blocked storage is a reader's setting, not a fault; logging it is noise
    } catch {
      // Blocked storage (private mode, cookies off) throws on read. The reader
      // gets the default view; what must not happen is the view never settling,
      // which would leave home waiting forever.
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect -- applies the localStorage view preference after hydration; must run post-mount (server has no localStorage) so a lazy initializer would cause an SSR/client hydration mismatch
    setSavedView(saved);
  }, []);

  // Settle once: a saved choice at once; otherwise the arm, or the grid when
  // the wait runs out. Never flips after settling, so home paints one view.
  useEffect(() => {
    if (searchViewModeSettled || savedView === undefined) return;
    if (savedView) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- settles the view from storage read in the effect above
      setSearchViewModeState(savedView);
      setSearchViewModeSettled(true);
      return;
    }
    // The arm comes from a cookie that UiTestProvider reads in the same commit
    // as the saved view above, so it is here by now: the one-second wait for a
    // missing arm dated from when the arm was fetched, and could no longer fire.
    // Were it ever null, this starts on the grid and counts no one -- where the
    // wait used to end.
    setSearchViewModeState(viewArm === 'showcase' ? 'showcase' : 'grid');
    setAppliedViewArm(viewArm);
    setSearchViewModeSettled(true);
  }, [searchViewModeSettled, savedView, viewArm]);

  // These two run from the menu and popup setters, which are only ever
  // called from handlers and effects -- in the browser, never in a server render.
  const handleS2nPlayer = useCallback((show: boolean) => {
    const sp = document.querySelector('.s2nPlayerContainer') as HTMLElement;
    if (sp) {
      sp.style.display = show ? 'none' : 'block';
    }
  }, []);

  const handleBodyOverflow = useCallback((hidden: boolean) => {
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = hidden ? 'hidden' : '';
    body.style.overflow = hidden ? 'hidden' : '';
  }, []);

  const setLoginOpen = useCallback((open: boolean) => {
    setLoginOpenState(open);
  }, []);

  const setProductOpen = useCallback((open: boolean) => {
    setProductOpenState(open);
    if (!open) {
      setChatOpenState(false);
      setShareMenuOpenState(false);
      setReportMenuOpenState(false);
    }
  }, []);

  const setChatOpen = useCallback(async (open: boolean, _id?: string | null) => {
    handleS2nPlayer(open);
    setChatOpenState(open);
    setShareMenuOpenState(false);
    setReportMenuOpenState(false);
    
    const action = open ? 'open' : 'close';
    googleEvent(`chat_${action}`);
  }, [handleS2nPlayer]);

  const setPopupOpen = useCallback((open: boolean) => {
    setPopupOpenState(open);
    handleBodyOverflow(open);
  }, [handleBodyOverflow]);

  const setPopupContents = useCallback((contents: ReactNode, clickCloseFunc?: (() => void) | null) => {
    setPopupContentsState(contents);
    setClickClose(() => clickCloseFunc || null);
  }, []);

  const setCameraHelpOpen = useCallback((open: boolean) => {
    setCameraHelpOpenState(open);
  }, []);

  const setReportMenuOpen = useCallback((open: boolean) => {
    handleS2nPlayer(open);
    setReportMenuOpenState(open);
    const action = open ? 'open' : 'close';
    googleEvent(`report_menu_${action}`);
  }, [handleS2nPlayer]);

  const setMessageMenuOpen = useCallback((open: boolean) => {
    handleS2nPlayer(open);
    setMessageMenuOpenState(open);
    if (open) {
      setChatMenuOpenState(false);
      setChatUserMenuOpenState(false);
    }
    const action = open ? 'open' : 'close';
    googleEvent(`message_menu_${action}`);
  }, [handleS2nPlayer]);

  const setChatMenuOpen = useCallback((open: boolean) => {
    setChatMenuOpenState(open);
    if (open) {
      setMessageMenuOpenState(false);
      setChatUserMenuOpenState(false);
    }
    const action = open ? 'open' : 'close';
    googleEvent(`chat_menu_${action}`);
  }, []);

  const setChatUserMenuOpen = useCallback((open: boolean) => {
    setChatUserMenuOpenState(open);
    if (open) {
      setMessageMenuOpenState(false);
      setChatMenuOpenState(false);
    }
    const action = open ? 'open' : 'close';
    googleEvent(`chat_user_menu_${action}`);
  }, []);

  const setShareMenuOpen = useCallback((open: boolean) => {
    handleS2nPlayer(open);
    setShareMenuOpenState(open);
    const action = open ? 'open' : 'close';
    googleEvent(`share_button_${action}`);
  }, [handleS2nPlayer]);

  const setCountryCodeOpen = useCallback((open: boolean) => {
    setCountryCodeOpenState(open);
    const action = open ? 'open' : 'close';
    googleEvent(`country_code_popup_${action}`);
  }, []);

  const setShippingDrawerOpen = useCallback((open: boolean) => {
    setShippingDrawerOpenState(open);
    const action = open ? 'open' : 'close';
    googleEvent(`shipping_drawer_${action}`);
  }, []);

  const setCategoryDrawerOpen = useCallback((open: boolean) => {
    setCategoryDrawerOpenState(open);
    const action = open ? 'open' : 'close';
    googleEvent(`category_drawer_${action}`);
  }, []);

  const setFilterDrawerOpen = useCallback((open: boolean) => {
    setFilterDrawerOpenState(open);
    googleEvent(`filter_drawer_${open ? 'open' : 'close'}`);
  }, []);

  const setNotificationDrawerOpen = useCallback((open: boolean) => {
    setNotificationDrawerOpenState(open);
    const action = open ? 'open' : 'close';
    googleEvent(`notification_drawer_${action}`);
  }, []);

  const setLoginView = useCallback((view: LoginView) => {
    setLoginViewState(view);
  }, []);

  const setLoginPrefill = useCallback((identifier: string | null) => {
    setLoginPrefillState(identifier);
  }, []);

  const setPostViewState = useCallback((state: PostViewState) => {
    googleEvent(`sell_step_${state}`);
    setPostViewStateState(state);
  }, []);

  const setMobileTopNav = useCallback((nav: MobileTopNav) => {
    setMobileTopNavState(nav);
  }, []);

  const setMobileBottomNav = useCallback((nav: string) => {
    setMobileBottomNavState(nav);
  }, []);

  const setSearchFocus = useCallback((focused: boolean) => {
    setSearchFocusedState(focused);
  }, []);

  const setUploadSpinner = useCallback((show: boolean) => {
    setUploadSpinnerState(show);
  }, []);

  const setSearchViewMode = useCallback((mode: SearchViewMode) => {
    setSearchViewModeState(mode);
    // Chosen by a click, so this is the browser.
    localStorage.setItem('search-view-preference', mode);
    googleEvent(`search_view_${mode}`);
  }, []);

  const value: UIContextType = {
    // States
    loginOpen,
    productOpen,
    chatOpen,
    popupOpen,
    popupContents,
    clickClose,
    cameraHelpOpen,
    reportMenuOpen,
    messageMenuOpen,
    chatMenuOpen,
    chatUserMenuOpen,
    shareMenuOpen,
    countryCodeOpen,
    shippingDrawerOpen,
    categoryDrawerOpen,
    filterDrawerOpen,
    notificationDrawerOpen,
    loginView,
    loginPrefill,
    postViewState,
    mobileTopNav,
    mobileBottomNav,
    searchFocused,
    uploadSpinner,
    searchViewMode,
    searchViewModeSettled,

    // Actions
    setLoginOpen,
    setLoginPrefill,
    setProductOpen,
    setChatOpen,
    setPopupOpen,
    setPopupContents,
    setCameraHelpOpen,
    setReportMenuOpen,
    setMessageMenuOpen,
    setChatMenuOpen,
    setChatUserMenuOpen,
    setShareMenuOpen,
    setCountryCodeOpen,
    setShippingDrawerOpen,
    setCategoryDrawerOpen,
    setFilterDrawerOpen,
    setNotificationDrawerOpen,
    setLoginView,
    setPostViewState,
    setMobileTopNav,
    setMobileBottomNav,
    setSearchFocus,
    setUploadSpinner,
    setSearchViewMode,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export default UIProvider;